import * as XLSX from 'xlsx'

const IDENTIFIER_HEADERS = [
  'unique_identifier',
  'uniqueidentifier',
  'unique id',
  'unique_id',
  'identifier',
  'id',
  'contact_number',
  'contact number',
  'phone',
  'phone_number',
]

const EMAIL_HEADERS = [
  'agent_email',
  'agentemail',
  'agent email',
  'email',
  'user_email',
  'user email',
]

function normalizeHeader(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
}

function findColumnIndex(headers, candidates) {
  const normalized = headers.map(normalizeHeader)
  for (const candidate of candidates) {
    const needle = normalizeHeader(candidate)
    const idx = normalized.findIndex(
      (header) => header === needle || header.replace(/\s/g, '') === needle.replace(/\s/g, ''),
    )
    if (idx >= 0) return idx
  }
  return -1
}

function cellText(value) {
  if (value == null) return ''
  if (typeof value === 'number' && Number.isFinite(value)) {
    // Avoid scientific notation for phone-like identifiers from Excel.
    return String(value)
  }
  return String(value).trim()
}

/**
 * Parse a browser File (.xlsx / .xls / .csv) into
 * [{ unique_identifier, agent_email }, ...].
 * Used to build the CSV uploaded to the presigned S3 URL (not sent via GraphQL).
 */
export async function parseExcelAssignmentsFile(file) {
  if (!file) {
    return { ok: false, error: 'Choose an Excel or CSV file.', assignments: [] }
  }

  try {
    const buffer = await file.arrayBuffer()
    const workbook = XLSX.read(buffer, { type: 'array' })
    const sheetName = workbook.SheetNames[0]
    if (!sheetName) {
      return { ok: false, error: 'The spreadsheet has no sheets.', assignments: [] }
    }

    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
      header: 1,
      defval: '',
      raw: false,
    })

    if (!Array.isArray(rows) || rows.length < 2) {
      return {
        ok: false,
        error: 'Spreadsheet needs a header row and at least one data row.',
        assignments: [],
      }
    }

    const headers = (rows[0] || []).map((cell) => cellText(cell))
    const idCol = findColumnIndex(headers, IDENTIFIER_HEADERS)
    const emailCol = findColumnIndex(headers, EMAIL_HEADERS)

    if (idCol < 0 || emailCol < 0) {
      return {
        ok: false,
        error:
          'Columns required: unique_identifier (or contact_number) and agent_email.',
        assignments: [],
      }
    }

    const assignments = []
    const seen = new Set()

    for (let i = 1; i < rows.length; i += 1) {
      const row = rows[i]
      if (!Array.isArray(row)) continue
      const unique_identifier = cellText(row[idCol])
      const agent_email = cellText(row[emailCol]).toLowerCase()
      if (!unique_identifier && !agent_email) continue
      if (!unique_identifier || !agent_email) continue

      const key = `${unique_identifier.toLowerCase()}::${agent_email}`
      if (seen.has(key)) continue
      seen.add(key)

      assignments.push({ unique_identifier, agent_email })
    }

    if (!assignments.length) {
      return {
        ok: false,
        error: 'No valid rows found (need unique_identifier + agent_email).',
        assignments: [],
      }
    }

    return { ok: true, assignments, error: '' }
  } catch (error) {
    return {
      ok: false,
      error: error?.message || 'Failed to parse spreadsheet.',
      assignments: [],
    }
  }
}

/** Escape a CSV cell (RFC-style quotes when needed). */
function csvEscape(value) {
  const text = String(value ?? '')
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

/**
 * Build a text/csv body with columns unique_identifier, agent_email
 * for PUT to the createTask upload_url.
 */
export function assignmentsToCsv(assignments) {
  const rows = Array.isArray(assignments) ? assignments : []
  const lines = ['unique_identifier,agent_email']
  for (const row of rows) {
    const unique_identifier = String(
      row?.unique_identifier ?? row?.uniqueIdentifier ?? '',
    ).trim()
    const agent_email = String(row?.agent_email ?? row?.agentEmail ?? '')
      .trim()
      .toLowerCase()
    if (!unique_identifier || !agent_email) continue
    lines.push(`${csvEscape(unique_identifier)},${csvEscape(agent_email)}`)
  }
  return `${lines.join('\n')}\n`
}

/** Normalize unmatched rows from createTask / getTaskAssignmentUploadStatus. */
export function normalizeUnmatchedAssignments(payload) {
  const root =
    payload?.data && typeof payload.data === 'object' ? payload.data : payload
  const list =
    root?.unmatched ??
    root?.unmatched_assignments ??
    root?.unmatchedAssignments ??
    []
  if (!Array.isArray(list)) return []
  return list
    .map((row) => ({
      unique_identifier: String(
        row?.unique_identifier ?? row?.uniqueIdentifier ?? '',
      ).trim(),
      agent_email: String(row?.agent_email ?? row?.agentEmail ?? '').trim(),
      reason: String(row?.reason ?? row?.message ?? 'Unmatched').trim(),
    }))
    .filter((row) => row.unique_identifier || row.agent_email || row.reason)
}

/** Normalize getTaskAssignmentUploadStatus payload. */
export function normalizeAssignmentUploadStatus(payload) {
  const root =
    payload?.data && typeof payload.data === 'object' ? payload.data : payload
  if (!root || typeof root !== 'object') {
    return {
      uploadId: '',
      status: '',
      matchedCount: null,
      unmatchedCount: null,
      alreadyPresentCount: null,
      unmatched: [],
    }
  }
  const status = String(root.status ?? '').trim().toUpperCase()
  const matched = Number(root.matched_count ?? root.matchedCount)
  const unmatchedCount = Number(root.unmatched_count ?? root.unmatchedCount)
  const alreadyPresent = Number(
    root.already_present_count ?? root.alreadyPresentCount,
  )
  return {
    uploadId: String(root.upload_id ?? root.uploadId ?? '').trim(),
    status,
    matchedCount: Number.isFinite(matched) ? matched : null,
    unmatchedCount: Number.isFinite(unmatchedCount) ? unmatchedCount : null,
    alreadyPresentCount: Number.isFinite(alreadyPresent) ? alreadyPresent : null,
    unmatched: normalizeUnmatchedAssignments(root),
  }
}
